import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../lib/supabaseClient';
import { createDoctorProfile, getCountryCodes, getDegrees, getUniversities, getCouncils, getSpecialties, getPrescriptionStyle } from '../api/doctorService';
import lodashDebounce from 'lodash.debounce';

export const useProfileStore = create(
  persist(
    (set, get) => ({
      // --- STATE ---
      profileStatus: null, 
      draftProfile: {}, 
      prescriptionStyle: null,
      isLoadingStyle: true,
      currentStep: 1,
      activeSection: 1,
      tourStep: 0,
      isTourRunning: false,
      isLoading: true,
      isError: false,
      utils: {
        countryCodes: [],
        degrees: [],
        universities: [],
        councils: [],
        specialties: [],
      },
      isLoadingUtils: true,
      isReviewModalOpen: false,

      // --- ACTIONS ---

      setActiveSection: (section) => set({ activeSection: section }),
      setTourStep: (step) => set({ tourStep: step, isTourRunning: true }),

      stopTour: () => set({ isTourRunning: false }),

      completeTourSynchronously: () => {
          const currentProfile = get().profileStatus;
          if (currentProfile) {
              set({
                  profileStatus: { ...currentProfile, has_completed_tour: true },
                  tourStep: 0,
                  isTourRunning: false 
              });
          } else {
              // Failsafe in case profile hasn't loaded
              set({ tourStep: 0, isTourRunning: false });
          }
      },

      updateDraftField: (field, value) => {
        set(state => ({
          draftProfile: { ...state.draftProfile, [field]: value }
        }));
      },

      setSignatureUrl: (filePath) => {
        set(state => ({
          profileStatus: { ...state.profileStatus, signature_url: filePath },
          draftProfile: { ...state.draftProfile, signature_url: filePath },
        }));
      },

      setPrescriptionStyle: (style) => set({ prescriptionStyle: style }),

      setProfileStatus: (newProfile) => set({ 
          profileStatus: newProfile,
          // Also update the draft to keep them in sync after a save
          draftProfile: newProfile 
      }),

      openReviewModal: () => set({ isReviewModalOpen: true }),
      closeReviewModal: () => set({ isReviewModalOpen: false }),

      isRegistrationRequired: () => {
        const { draftProfile, utils } = get();
        if (!draftProfile.specialties || draftProfile.specialties.length === 0) {
            return true; 
        }
        return draftProfile.specialties.some(specId => {
            const specialty = utils.specialties.find(s => s.id === specId);
            return specialty?.requires_license === true;
        });
      },

      fetchUtils: async () => {
        if (!get().isLoadingUtils || get().utils.countryCodes.length > 0) return;
        try {
          set({ isLoadingUtils: true });
          const [countryCodes, degrees, universities, councils, specialties] = await Promise.all([
            getCountryCodes(), getDegrees(), getUniversities(), getCouncils(), getSpecialties()
          ]);
          set({ utils: { countryCodes, degrees, universities, councils, specialties }, isLoadingUtils: false });
        } catch (error) {
          console.error("Failed to fetch utility data:", error);
          set({ isError: true, isLoadingUtils: false });
        }
      },

      createProfile: async (formData) => {
        set({ isLoading: true });
        try {
          await createDoctorProfile(formData);
          await get().fetchInitialStatus(true);

        } catch (error) {
          console.error("Error creating profile:", error);
          set({ isError: true });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },


fetchInitialStatus: async (force = false) => {
    // No change to this initial check
    if (get().profileStatus && !force) {
        set({ isLoading: false });
        return;
    }

    set({ isLoading: true, isLoadingStyle: true });
    try {
        await get().fetchUtils();

        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) throw new Error("No authenticated user found.");

        const { data, error } = await supabase.rpc('get_doctor_full_profile', {
            p_doctor_id: user.id
        });

        if (error) throw error;

        const styleData = await getPrescriptionStyle();
        set({ prescriptionStyle: styleData, isLoadingStyle: false });

        if (data) {
            // Case 1: Existing User
            // The user has a profile, so we set the state from the database.
            set({
                profileStatus: data,
                draftProfile: data, // Pre-fill draft with existing data
                currentStep: get().currentStep || data?.current_step || 1,
            });
        } else {
            // Case 2: New User
            // The RPC returned null, so we know this is a new user who needs onboarding.
            set({
                profileStatus: { profile_status: 'onboarding' },
                draftProfile: {}, // Ensure draft is empty for a new user
                currentStep: 1,
            });
        }

    } catch (error) {
        console.error("Error fetching initial profile status:", error);
        set({ isError: true, profileStatus: null, draftProfile: {} });
    } finally {
        set({ isLoading: false });
    }
},

      updateProfileStep: async (stepName, stepData) => {
        const doctorId = get().profileStatus?.id;
        if (!doctorId && stepName !== 'personal_details') {
          throw new Error("Cannot update step, doctorId is missing from the store.");
        }
        try {
        const { data: fullUpdatedProfile, error } = await supabase.rpc('update_doctor_profile_step', {
            p_doctor_id: doctorId,
            p_step_name: stepName,
            p_step_data: stepData,
        });

        if (error) throw error;

        
        set(state => ({
            
            profileStatus: fullUpdatedProfile,
            draftProfile: {
                ...state.draftProfile,
                ...fullUpdatedProfile, 
            }
        }));

        return fullUpdatedProfile;

    } catch (error) {
        console.error(`Error updating step ${stepName}:`, error);
        throw error;
    }
},
      
      
      setCurrentStep: (step) => {
        if (step === 3 && !get().isRegistrationRequired()) {
            set({ currentStep: 4 });
        } else {
            set({ currentStep: step });
        }
      },
    }),
    {
      name: 'doctor-profile-storage', 
      storage: createJSONStorage(() => localStorage), 
      partialize: (state) => ({
        draftProfile: state.draftProfile,
        currentStep: state.currentStep,
      }),
    }
  )
);


export const useDatabaseStore = create((set, get) => ({
  // --- STATE ---
  activeSource: 'Patients',
  searchTerm: '',
  debouncedSearchTerm: '',
  timeFilter: 'all', 

  // --- ACTIONS ---
  setActiveSource: (source) => set({ 
    activeSource: source, 
    searchTerm: '', 
    debouncedSearchTerm: '',
    timeFilter: 'all' 
  }),
  
  setTimeFilter: (filter) => set({ timeFilter: filter }),

  setSearchTerm: lodashDebounce((term) => {
    set({ debouncedSearchTerm: term });
  }, 300),

  handleSearchInputChange: (e) => {
    const term = e.target.value;
    set({ searchTerm: term });
    get().setSearchTerm(term);
  },
}));